import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
  Shield, Eye, FileText, UserX, Download, Clock, CheckCircle, 
  XCircle, AlertTriangle, Users, Building2, RefreshCw, Loader2,
  History, Lock, Unlock, Search, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const TIPOS_CONSENTIMENTO = [
  { codigo: 'Dados_Basicos', nome: 'Dados Básicos', descricao: 'Processamento de dados de identificação' },
  { codigo: 'Dados_Utentes', nome: 'Dados de Utentes', descricao: 'Processamento de dados agregados de utentes' },
  { codigo: 'Comunicacoes', nome: 'Comunicações', descricao: 'Envio de comunicações e newsletters' },
  { codigo: 'Partilha_Terceiros', nome: 'Partilha com Terceiros', descricao: 'Partilha de dados com entidades parceiras' }
];

const TIPOS_PEDIDO = [
  { codigo: 'Acesso_Dados', nome: 'Acesso aos Dados', descricao: 'Direito de acesso (Art. 15)' },
  { codigo: 'Retificacao', nome: 'Retificação', descricao: 'Direito de retificação (Art. 16)' },
  { codigo: 'Eliminacao', nome: 'Eliminação', descricao: 'Direito ao apagamento (Art. 17)' },
  { codigo: 'Portabilidade', nome: 'Portabilidade', descricao: 'Direito à portabilidade (Art. 20)' },
  { codigo: 'Oposicao', nome: 'Oposição', descricao: 'Direito de oposição (Art. 21)' }
];

const STATUS_COLORS = {
  'Pendente': 'bg-amber-500',
  'Em_Processamento': 'bg-blue-500',
  'Concluido': 'bg-green-500',
  'Rejeitado': 'bg-red-500'
};

export default function RGPDPage() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [consentimentos, setConsentimentos] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [acessos, setAcessos] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  
  // Filters
  const [filtroInstituicao, setFiltroInstituicao] = useState('');
  
  // Modals
  const [consentimentoModalOpen, setConsentimentoModalOpen] = useState(false);
  const [pedidoModalOpen, setPedidoModalOpen] = useState(false);
  const [pedidoDetailModal, setPedidoDetailModal] = useState(false);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [esquecimentoModalOpen, setEsquecimentoModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  
  // Forms
  const [consentimentoForm, setConsentimentoForm] = useState({
    instituicao_id: '',
    tipo_consentimento: '',
    aceite: true
  });
  const [pedidoForm, setPedidoForm] = useState({
    instituicao_id: '',
    tipo_pedido: '',
    descricao: '',
    dados_solicitados: ''
  });
  const [esquecimentoInstituicao, setEsquecimentoInstituicao] = useState('');
  const [exportInstituicao, setExportInstituicao] = useState('');
  const [respostaText, setRespostaText] = useState('');

  useEffect(() => {
    loadData();
  }, [filtroInstituicao]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const params = new URLSearchParams();
      if (filtroInstituicao) params.append('instituicao_id', filtroInstituicao);
      
      const [dashRes, consentRes, pedidosRes, acessosRes, instRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd/dashboard`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd/consentimentos?${params}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd/pedidos?${params}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd/acessos?limit=50`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instituicoes`, { headers })
      ]);
      
      const [dashData, consentData, pedidosData, acessosData, instData] = await Promise.all([
        dashRes.json(),
        consentRes.json(),
        pedidosRes.json(),
        acessosRes.json(),
        instRes.json()
      ]);
      
      setDashboard(dashData);
      setConsentimentos(consentData);
      setPedidos(pedidosData);
      setAcessos(acessosData);
      setInstituicoes(instData);
    } catch (error) {
      console.error('Error loading RGPD data:', error);
      toast.error('Erro ao carregar dados RGPD');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistarConsentimento = async () => {
    if (!consentimentoForm.instituicao_id || !consentimentoForm.tipo_consentimento) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd/consentimentos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(consentimentoForm)
      });
      
      if (response.ok) {
        toast.success('Consentimento registado');
        setConsentimentoModalOpen(false);
        setConsentimentoForm({ instituicao_id: '', tipo_consentimento: '', aceite: true });
        loadData();
      } else {
        toast.error('Erro ao registar consentimento');
      }
    } catch (error) {
      toast.error('Erro ao registar consentimento');
    }
  };

  const handleRevogarConsentimento = async (id) => {
    if (!confirm('Tem certeza que deseja revogar este consentimento?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd/consentimentos/${id}/revogar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Consentimento revogado');
        loadData();
      } else {
        toast.error('Erro ao revogar');
      }
    } catch (error) {
      toast.error('Erro ao revogar');
    }
  };

  const handleCriarPedido = async () => {
    if (!pedidoForm.instituicao_id || !pedidoForm.tipo_pedido || !pedidoForm.descricao) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd/pedidos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...pedidoForm,
          dados_solicitados: pedidoForm.dados_solicitados.split(',').map(s => s.trim()).filter(s => s)
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Pedido ${data.codigo} criado. Prazo: ${new Date(data.prazo_resposta).toLocaleDateString('pt-PT')}`);
        setPedidoModalOpen(false);
        setPedidoForm({ instituicao_id: '', tipo_pedido: '', descricao: '', dados_solicitados: '' });
        loadData();
      } else {
        toast.error('Erro ao criar pedido');
      }
    } catch (error) {
      toast.error('Erro ao criar pedido');
    }
  };

  const handleProcessarPedido = async (status) => {
    if (!selectedPedido) return;
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ status });
      if (respostaText) params.append('resposta', respostaText);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd/pedidos/${selectedPedido.id}/processar?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`Pedido atualizado para ${status}`);
        setPedidoDetailModal(false);
        setSelectedPedido(null);
        setRespostaText('');
        loadData();
      } else {
        toast.error('Erro ao processar pedido');
      }
    } catch (error) {
      toast.error('Erro ao processar pedido');
    }
  };

  const handleDireitoEsquecimento = async () => {
    if (!esquecimentoInstituicao) {
      toast.error('Selecione uma instituição');
      return;
    }
    
    if (!confirm('ATENÇÃO: Esta ação irá ANONIMIZAR permanentemente todos os dados desta instituição. Esta ação é IRREVERSÍVEL. Deseja continuar?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd/direito-esquecimento/${esquecimentoInstituicao}?confirmar=true`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Dados anonimizados. ${data.documentos_arquivados} documentos arquivados.`);
        setEsquecimentoModalOpen(false);
        setEsquecimentoInstituicao('');
        loadData();
      } else {
        toast.error('Erro ao executar');
      }
    } catch (error) {
      toast.error('Erro ao executar');
    }
  };

  const handleExportarDados = async () => {
    if (!exportInstituicao) {
      toast.error('Selecione uma instituição');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rgpd/exportar-dados/${exportInstituicao}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Download as JSON
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dados_rgpd_${exportInstituicao}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        toast.success('Dados exportados');
        setExportModalOpen(false);
        setExportInstituicao('');
      } else {
        toast.error('Erro na exportação');
      }
    } catch (error) {
      toast.error('Erro na exportação');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="rgpd-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="rgpd-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Shield className="w-8 h-8 text-green-600" />
            Conformidade RGPD
          </h1>
          <p className="text-slate-500 mt-1">Gestão de consentimentos, pedidos e direitos dos titulares</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Dashboard KPIs */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className={dashboard.alertas?.pedidos_atrasados ? 'border-red-300' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${dashboard.alertas?.pedidos_atrasados ? 'bg-red-500' : 'bg-amber-500'}`}>
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.pedidos?.pendentes || 0}</p>
                  <p className="text-xs text-slate-500">Pedidos Pendentes</p>
                </div>
              </div>
              {dashboard.pedidos?.atrasados > 0 && (
                <Badge className="mt-2 bg-red-500">{dashboard.pedidos.atrasados} fora do prazo!</Badge>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.consentimentos?.aceites || 0}</p>
                  <p className="text-xs text-slate-500">Consentimentos Ativos</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Taxa aceitação: {dashboard.consentimentos?.taxa_aceitacao || 0}%
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dashboard.acessos?.hoje || 0}</p>
                  <p className="text-xs text-slate-500">Acessos Hoje</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="pt-4">
              <div className="text-center">
                <Shield className="w-8 h-8 mx-auto text-green-600 mb-2" />
                <p className="text-sm font-medium text-green-700">
                  {dashboard.alertas?.pedidos_atrasados ? 'Ação Necessária' : 'Em Conformidade'}
                </p>
                <p className="text-xs text-slate-500 mt-1">{dashboard.alertas?.mensagem}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Button 
          variant="outline" 
          className="h-auto py-4 flex-col gap-2"
          onClick={() => setConsentimentoModalOpen(true)}
          data-testid="btn-novo-consentimento"
        >
          <Lock className="w-5 h-5 text-green-600" />
          <span>Novo Consentimento</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex-col gap-2"
          onClick={() => setPedidoModalOpen(true)}
          data-testid="btn-novo-pedido"
        >
          <FileText className="w-5 h-5 text-blue-600" />
          <span>Novo Pedido</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex-col gap-2"
          onClick={() => setExportModalOpen(true)}
          data-testid="btn-exportar"
        >
          <Download className="w-5 h-5 text-purple-600" />
          <span>Exportar Dados</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-4 flex-col gap-2 border-red-200 hover:bg-red-50"
          onClick={() => setEsquecimentoModalOpen(true)}
          data-testid="btn-esquecimento"
        >
          <UserX className="w-5 h-5 text-red-600" />
          <span>Direito Esquecimento</span>
        </Button>
      </div>

      <Tabs defaultValue="consentimentos" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="consentimentos">Consentimentos</TabsTrigger>
          <TabsTrigger value="pedidos" className="relative">
            Pedidos
            {dashboard?.pedidos?.pendentes > 0 && (
              <Badge className="ml-2 bg-amber-500">{dashboard.pedidos.pendentes}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="acessos">Histórico Acessos</TabsTrigger>
        </TabsList>

        {/* Consentimentos Tab */}
        <TabsContent value="consentimentos" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Consentimentos Ativos</CardTitle>
                <Select value={filtroInstituicao || "all"} onValueChange={(v) => setFiltroInstituicao(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filtrar instituição" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {instituicoes.map(inst => (
                      <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {consentimentos.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Lock className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nenhum consentimento registado</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Instituição</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Versão</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {consentimentos.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {c.instituicao_nome || c.instituicao_id}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {TIPOS_CONSENTIMENTO.find(t => t.codigo === c.tipo_consentimento)?.nome || c.tipo_consentimento}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {c.aceite ? (
                            <Badge className="bg-green-500">
                              <CheckCircle className="w-3 h-3 mr-1" /> Aceite
                            </Badge>
                          ) : (
                            <Badge className="bg-red-500">
                              <XCircle className="w-3 h-3 mr-1" /> Recusado
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(c.data_consentimento).toLocaleDateString('pt-PT')}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {c.versao_politica}
                        </TableCell>
                        <TableCell className="text-right">
                          {c.aceite && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500"
                              onClick={() => handleRevogarConsentimento(c.id)}
                            >
                              <Unlock className="w-4 h-4 mr-1" />
                              Revogar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pedidos Tab */}
        <TabsContent value="pedidos" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Pedidos RGPD</CardTitle>
              <CardDescription>
                Prazo legal de resposta: 30 dias (Art. 12 RGPD)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pedidos.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nenhum pedido registado</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Código</TableHead>
                      <TableHead>Instituição</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Data Pedido</TableHead>
                      <TableHead>Prazo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pedidos.map((p) => {
                      const prazoDate = new Date(p.prazo_resposta);
                      const hoje = new Date();
                      const diasRestantes = Math.ceil((prazoDate - hoje) / (1000 * 60 * 60 * 24));
                      const atrasado = diasRestantes < 0 && p.status === 'Pendente';
                      
                      return (
                        <TableRow key={p.id} className={atrasado ? 'bg-red-50' : ''}>
                          <TableCell className="font-mono text-sm">{p.codigo}</TableCell>
                          <TableCell>{p.instituicao_nome || p.instituicao_id}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {TIPOS_PEDIDO.find(t => t.codigo === p.tipo_pedido)?.nome || p.tipo_pedido}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(p.data_pedido).toLocaleDateString('pt-PT')}
                          </TableCell>
                          <TableCell>
                            <div className={`text-sm ${atrasado ? 'text-red-600 font-medium' : diasRestantes <= 7 ? 'text-amber-600' : ''}`}>
                              {prazoDate.toLocaleDateString('pt-PT')}
                              {p.status === 'Pendente' && (
                                <p className="text-xs">
                                  {atrasado ? `${Math.abs(diasRestantes)} dias em atraso!` : `${diasRestantes} dias restantes`}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[p.status] || 'bg-slate-500'}>
                              {p.status?.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setSelectedPedido(p); setPedidoDetailModal(true); }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Acessos Tab */}
        <TabsContent value="acessos" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-slate-500" />
                Histórico de Acessos a Dados
              </CardTitle>
              <CardDescription>
                Registo de todas as operações sobre dados pessoais
              </CardDescription>
            </CardHeader>
            <CardContent>
              {acessos.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Eye className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nenhum acesso registado</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Utilizador</TableHead>
                      <TableHead>Ação</TableHead>
                      <TableHead>Recurso</TableHead>
                      <TableHead>ID Recurso</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {acessos.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          {new Date(log.timestamp).toLocaleString('pt-PT')}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">{log.user_nome}</p>
                            <p className="text-xs text-slate-400">{log.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.acao}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{log.recurso_tipo}</TableCell>
                        <TableCell className="text-xs font-mono text-slate-500">
                          {log.recurso_id?.substring(0, 8)}...
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Novo Consentimento Modal */}
      <Dialog open={consentimentoModalOpen} onOpenChange={setConsentimentoModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registar Consentimento</DialogTitle>
            <DialogDescription>
              Registe o consentimento de uma instituição para processamento de dados
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Instituição *</Label>
              <Select 
                value={consentimentoForm.instituicao_id} 
                onValueChange={(v) => setConsentimentoForm(prev => ({ ...prev, instituicao_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a instituição" />
                </SelectTrigger>
                <SelectContent>
                  {instituicoes.map(inst => (
                    <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tipo de Consentimento *</Label>
              <Select 
                value={consentimentoForm.tipo_consentimento} 
                onValueChange={(v) => setConsentimentoForm(prev => ({ ...prev, tipo_consentimento: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_CONSENTIMENTO.map(tipo => (
                    <SelectItem key={tipo.codigo} value={tipo.codigo}>
                      {tipo.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {consentimentoForm.tipo_consentimento && (
                <p className="text-xs text-slate-500 mt-1">
                  {TIPOS_CONSENTIMENTO.find(t => t.codigo === consentimentoForm.tipo_consentimento)?.descricao}
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              <Label>Consentimento dado?</Label>
              <Button
                variant={consentimentoForm.aceite ? "default" : "outline"}
                size="sm"
                onClick={() => setConsentimentoForm(prev => ({ ...prev, aceite: true }))}
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Sim
              </Button>
              <Button
                variant={!consentimentoForm.aceite ? "destructive" : "outline"}
                size="sm"
                onClick={() => setConsentimentoForm(prev => ({ ...prev, aceite: false }))}
              >
                <XCircle className="w-4 h-4 mr-1" /> Não
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConsentimentoModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleRegistarConsentimento}>Registar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Novo Pedido Modal */}
      <Dialog open={pedidoModalOpen} onOpenChange={setPedidoModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Criar Pedido RGPD</DialogTitle>
            <DialogDescription>
              Registe um pedido de exercício de direitos do titular
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Instituição *</Label>
              <Select 
                value={pedidoForm.instituicao_id} 
                onValueChange={(v) => setPedidoForm(prev => ({ ...prev, instituicao_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a instituição" />
                </SelectTrigger>
                <SelectContent>
                  {instituicoes.map(inst => (
                    <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tipo de Pedido *</Label>
              <Select 
                value={pedidoForm.tipo_pedido} 
                onValueChange={(v) => setPedidoForm(prev => ({ ...prev, tipo_pedido: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_PEDIDO.map(tipo => (
                    <SelectItem key={tipo.codigo} value={tipo.codigo}>
                      {tipo.nome} - {tipo.descricao}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Descrição do Pedido *</Label>
              <Textarea
                value={pedidoForm.descricao}
                onChange={(e) => setPedidoForm(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Descreva o pedido em detalhe..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Dados Solicitados (separados por vírgula)</Label>
              <Input
                value={pedidoForm.dados_solicitados}
                onChange={(e) => setPedidoForm(prev => ({ ...prev, dados_solicitados: e.target.value }))}
                placeholder="Ex: nome, email, histórico"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPedidoModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCriarPedido}>Criar Pedido</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pedido Detail Modal */}
      <Dialog open={pedidoDetailModal} onOpenChange={setPedidoDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do Pedido {selectedPedido?.codigo}</DialogTitle>
          </DialogHeader>
          
          {selectedPedido && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Tipo</Label>
                  <p>{TIPOS_PEDIDO.find(t => t.codigo === selectedPedido.tipo_pedido)?.nome}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <Badge className={STATUS_COLORS[selectedPedido.status]}>
                    {selectedPedido.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <Label className="text-slate-500">Data Pedido</Label>
                  <p>{new Date(selectedPedido.data_pedido).toLocaleDateString('pt-PT')}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Prazo Legal</Label>
                  <p>{new Date(selectedPedido.prazo_resposta).toLocaleDateString('pt-PT')}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-500">Descrição</Label>
                <p className="text-sm">{selectedPedido.descricao}</p>
              </div>
              
              {selectedPedido.dados_solicitados?.length > 0 && (
                <div>
                  <Label className="text-slate-500">Dados Solicitados</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedPedido.dados_solicitados.map((d, i) => (
                      <Badge key={i} variant="outline">{d}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedPedido.status === 'Pendente' && (
                <div className="border-t pt-4 space-y-3">
                  <Label>Processar Pedido</Label>
                  <Textarea
                    placeholder="Resposta ao pedido (obrigatório para concluir)"
                    value={respostaText}
                    onChange={(e) => setRespostaText(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => handleProcessarPedido('Em_Processamento')}
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      Em Processamento
                    </Button>
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleProcessarPedido('Concluido')}
                      disabled={!respostaText}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Concluir
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={() => handleProcessarPedido('Rejeitado')}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Rejeitar
                    </Button>
                  </div>
                </div>
              )}
              
              {selectedPedido.resposta && (
                <div>
                  <Label className="text-slate-500">Resposta</Label>
                  <p className="text-sm bg-slate-50 p-3 rounded">{selectedPedido.resposta}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPedidoDetailModal(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Direito ao Esquecimento Modal */}
      <Dialog open={esquecimentoModalOpen} onOpenChange={setEsquecimentoModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Direito ao Esquecimento
            </DialogTitle>
            <DialogDescription>
              <strong className="text-red-600">ATENÇÃO:</strong> Esta ação irá anonimizar permanentemente todos os dados da instituição selecionada. Esta ação é IRREVERSÍVEL.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Instituição a Anonimizar</Label>
              <Select value={esquecimentoInstituicao} onValueChange={setEsquecimentoInstituicao}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a instituição" />
                </SelectTrigger>
                <SelectContent>
                  {instituicoes.map(inst => (
                    <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-sm">
              <p className="font-medium text-red-700">Esta ação irá:</p>
              <ul className="list-disc list-inside text-red-600 mt-2 space-y-1">
                <li>Anonimizar nome, email, telefone e morada</li>
                <li>Arquivar todos os documentos associados</li>
                <li>Eliminar ficheiros do servidor</li>
                <li>Manter registo de anonimização para auditoria</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEsquecimentoModalOpen(false)}>Cancelar</Button>
            <Button 
              variant="destructive" 
              onClick={handleDireitoEsquecimento}
              disabled={!esquecimentoInstituicao}
            >
              <UserX className="w-4 h-4 mr-2" />
              Executar Anonimização
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exportar Dados Modal */}
      <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exportar Dados (Portabilidade)</DialogTitle>
            <DialogDescription>
              Exporta todos os dados de uma instituição em formato JSON
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label>Instituição</Label>
            <Select value={exportInstituicao} onValueChange={setExportInstituicao}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a instituição" />
              </SelectTrigger>
              <SelectContent>
                {instituicoes.map(inst => (
                  <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleExportarDados} disabled={!exportInstituicao}>
              <Download className="w-4 h-4 mr-2" />
              Exportar JSON
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
